/**
 * Loads the audit problem-type registry (GET /audit/problem-types) once per
 * session and shares it across consumers via a module-level cache. The backend
 * registry is the single source of truth for labels, meanings, actions, and
 * severities — no frontend mirror (audit-remodel plan, Phase 2).
 */
import { useEffect, useState } from "react"

import { getProblemTypes, ProblemTypeInfo } from "../api/audit"

export type ProblemTypeMap = Record<string, ProblemTypeInfo>

let cachedRegistry: ProblemTypeMap | null = null
let inflightRequest: Promise<ProblemTypeMap> | null = null

async function loadRegistry(): Promise<ProblemTypeMap> {
  if (cachedRegistry) return cachedRegistry
  if (!inflightRequest) {
    inflightRequest = getProblemTypes().then((response) => {
      cachedRegistry = Object.fromEntries(
        response.problem_types.map((entry) => [entry.slug, entry])
      )
      return cachedRegistry
    })
  }
  return inflightRequest
}

/** Returns the slug → info map, or null while loading / on failure.
 *  Consumers fall back to rendering the raw slug when null. */
export function useProblemTypes(): ProblemTypeMap | null {
  const [registry, setRegistry] = useState<ProblemTypeMap | null>(cachedRegistry)

  // Synchronize once with the shared cache; failures leave the map null so
  // callers degrade to raw-slug rendering rather than breaking the table.
  useEffect(() => {
    if (registry) return
    let cancelled = false
    loadRegistry()
      .then((map) => {
        if (!cancelled) setRegistry(map)
      })
      .catch(() => {
        inflightRequest = null
      })
    return () => {
      cancelled = true
    }
  }, [registry])

  return registry
}
